import { useState } from 'react';
import { ArrowDown, ArrowUp, Lock, Trash2 } from 'lucide-react';
import { Button, IconButton, Select, TextField } from '@nyce/admin-kit/ui';
import {
  db,
  DB_IDENT,
  type CopyConfig,
  type CopyConfigInput,
  type CopyMethod,
  type DbServerRef,
  type Job,
  type PostRestoreScript,
} from '../../api/db';
import { EmptyState, ErrorBox, Loading, TableScroller, useAsync, useNotice } from '../../components/common';
import { EditorDialog, IdField, SLUG, useEditorForm } from '../../components/registry';
import { formatRelative } from '../../lib/format';
import {
  ConfirmDialog,
  DatabasePicker,
  ProtectedChip,
  serverName,
  Target,
  useServerDatabases,
  type DbScopes,
} from './shared';

type Form = CopyConfigInput & { id: string };

const empty: Form = {
  id: '',
  name: '',
  description: '',
  sourceServerId: '',
  sourceDatabase: '',
  targetServerId: '',
  targetDatabase: '',
  method: 'pg_dump_restore',
  postRestoreScripts: [],
};

const METHODS: { value: CopyMethod; label: string }[] = [
  { value: 'pg_dump_restore', label: 'pg_dump | pg_restore' },
  { value: 'pgcopydb', label: 'pgcopydb (parallel, large databases)' },
];

const toForm = ({ updatedAt: _u, updatedBy: _b, ...c }: CopyConfig): Form => ({
  ...c,
  postRestoreScripts: c.postRestoreScripts.map((s) => ({ ...s })),
});

/** Why the form cannot be saved yet, or null. Mirrors the API's copy-config rules. */
export function copyConfigProblem(v: Form, servers: DbServerRef[]): string | null {
  if (!v.name.trim()) return 'Name is required';
  if (!SLUG.test(v.id)) return 'Id must be a lowercase slug';
  if (!v.sourceServerId || !v.sourceDatabase) return 'Pick the source server and database';
  if (!v.targetServerId) return 'Pick the target server';
  if (servers.find((s) => s.id === v.targetServerId)?.protected)
    return 'The target server is protected and can never be a copy target';
  if (!DB_IDENT.test(v.targetDatabase))
    return 'Target database: lowercase letters, digits and _, starting with a letter or _ (max 63)';
  if (v.targetServerId === v.sourceServerId && v.targetDatabase === v.sourceDatabase)
    return 'Target must differ from the source';
  if (v.postRestoreScripts.some((s) => !s.name.trim() || !s.sql.trim()))
    return 'Every post-restore script needs a name and SQL';
  return null;
}

function ScriptsEditor({
  value,
  onChange,
}: {
  value: PostRestoreScript[];
  onChange: (v: PostRestoreScript[]) => void;
}) {
  const set = (i: number, patch: Partial<PostRestoreScript>) =>
    onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i: number, d: -1 | 1) => {
    const next = [...value];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div className="nyk-ui-field nyk-span2 dbx-scripts" data-testid="scripts">
      <span>Post-restore scripts</span>
      <span className="nyk-ui-hint">
        Run in order after the restore, each in its own transaction, on the target database.
      </span>
      {value.map((s, i) => (
        <div key={i} className="dbx-script" data-script={i}>
          <div className="dbx-script-head">
            <TextField
              aria-label={`Script ${i + 1} name`}
              placeholder={`Script ${i + 1} name`}
              value={s.name}
              onChange={(e) => set(i, { name: e.target.value })}
            />
            <IconButton label={`Move script ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
              <ArrowUp size={16} />
            </IconButton>
            <IconButton
              label={`Move script ${i + 1} down`}
              disabled={i === value.length - 1}
              onClick={() => move(i, 1)}
            >
              <ArrowDown size={16} />
            </IconButton>
            <IconButton
              label={`Remove script ${i + 1}`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              <Trash2 size={16} />
            </IconButton>
          </div>
          <TextField
            multiline
            aria-label={`Script ${i + 1} SQL`}
            className="dbx-sql-input"
            rows={4}
            spellCheck={false}
            placeholder="UPDATE settings SET value = 'staging' WHERE key = 'env';"
            value={s.sql}
            onChange={(e) => set(i, { sql: e.target.value })}
          />
        </div>
      ))}
      <div>
        <Button size="sm" variant="secondary" onClick={() => onChange([...value, { name: '', sql: '' }])}>
          Add script
        </Button>
      </div>
    </div>
  );
}

function ConfigForm({
  initial,
  isNew,
  servers,
  onSaved,
  onClose,
}: {
  initial: Form;
  isNew: boolean;
  servers: DbServerRef[];
  onSaved: (c: CopyConfig) => void;
  onClose: () => void;
}) {
  const f = useEditorForm<Form>(initial, isNew);
  const { v, set } = f;
  const src = useServerDatabases(v.sourceServerId);
  const problem = copyConfigProblem(v, servers);
  const protectedServers = servers.filter((s) => s.protected);
  const identBad = !!v.targetDatabase && !DB_IDENT.test(v.targetDatabase);
  const save = () =>
    void f.run(async () => {
      const { id, ...body } = v;
      onSaved(
        await db.copyConfigs.put(id, {
          ...body,
          name: body.name.trim(),
          postRestoreScripts: body.postRestoreScripts.map((s) => ({ name: s.name.trim(), sql: s.sql })),
        })
      );
    });
  return (
    <EditorDialog
      title={isNew ? 'New copy config' : `Copy config ${v.id}`}
      error={f.error}
      busy={f.busy}
      canSave={!problem}
      onSave={save}
      onClose={onClose}
      maxWidth={760}
    >
      <TextField label="Name" value={v.name} onChange={(e) => set('name', e.target.value)} required />
      <IdField value={v.id} isNew={isNew} onChange={f.setId} example="acme-prod-to-test" />
      <TextField
        label="Description"
        className="nyk-span2"
        value={v.description}
        onChange={(e) => set('description', e.target.value)}
      />
      <Select
        label="Source server"
        value={v.sourceServerId}
        placeholder="Pick a server"
        onChange={(id) => f.setV((c) => ({ ...c, sourceServerId: id, sourceDatabase: '' }))}
        options={servers.map((s) => ({ value: s.id, label: s.name || s.id }))}
      />
      <label className="nyk-ui-field">
        <span>Source database</span>
        <DatabasePicker
          label="Source database"
          databases={src.names}
          value={v.sourceDatabase}
          onChange={(d) => set('sourceDatabase', d)}
        />
        {src.error && <span className="nyk-ui-input-error">{src.error}</span>}
      </label>
      <label className="nyk-ui-field">
        <span>Target server</span>
        <Select
          aria-label="Target server"
          value={v.targetServerId}
          placeholder="Pick a server"
          onChange={(id) => set('targetServerId', id)}
          options={servers.map((s) => ({
            value: s.id,
            label: s.protected ? `${s.name || s.id} — protected` : s.name || s.id,
            disabled: s.protected,
          }))}
        />
        {!!protectedServers.length && (
          <span className="nyk-ui-hint dbx-lock-hint">
            <Lock size={12} aria-hidden="true" /> Protected servers (
            {protectedServers.map((s) => s.name || s.id).join(', ')}) are never copy targets.
          </span>
        )}
      </label>
      <TextField
        label="Target database"
        value={v.targetDatabase}
        onChange={(e) => set('targetDatabase', e.target.value)}
        error={identBad ? 'lowercase letters, digits and _, starting with a letter or _' : undefined}
        hint="dropped and re-created on every run"
        spellCheck={false}
      />
      <Select
        label="Method"
        value={v.method}
        onChange={(m) => set('method', m as CopyMethod)}
        options={METHODS}
      />
      <ScriptsEditor value={v.postRestoreScripts} onChange={(s) => set('postRestoreScripts', s)} />
      {problem && (v.name || !isNew) && <p className="nyk-ui-hint nyk-span2 dbx-form-problem">{problem}</p>}
    </EditorDialog>
  );
}

/** Type-the-name confirmation before a copy drops the target database. */
export function RunCopyDialog({
  config,
  servers,
  onClose,
  onStarted,
}: {
  config: CopyConfig;
  servers: DbServerRef[];
  onClose: () => void;
  onStarted: (job: Job) => void;
}) {
  const [typed, setTyped] = useState('');
  const target = serverName(servers, config.targetServerId);
  return (
    <ConfirmDialog
      title={`Run ${config.name || config.id}`}
      confirmLabel="Drop and copy"
      danger
      canConfirm={typed === config.targetDatabase}
      onClose={onClose}
      onConfirm={async () => onStarted(await db.jobs.copy(config.id, typed))}
    >
      <div className="dbx-danger" role="alert">
        This drops <b>{config.targetDatabase}</b> on <b>{target}</b> and replaces it with a copy of{' '}
        <b>
          {serverName(servers, config.sourceServerId)}/{config.sourceDatabase}
        </b>
        . Everything in {config.targetDatabase} is lost.
      </div>
      {!!config.postRestoreScripts.length && (
        <p className="dbx-muted">
          Then runs {config.postRestoreScripts.length} post-restore script
          {config.postRestoreScripts.length === 1 ? '' : 's'}:{' '}
          {config.postRestoreScripts.map((s) => s.name).join(', ')}.
        </p>
      )}
      <TextField
        label={
          <>
            Type <code>{config.targetDatabase}</code> to confirm
          </>
        }
        aria-label="Confirm target database"
        value={typed}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setTyped(e.target.value)}
      />
    </ConfirmDialog>
  );
}

export function CopyConfigsTab({
  servers,
  scopes,
  onJob,
}: {
  servers: DbServerRef[];
  scopes: DbScopes;
  onJob: (job: Job) => void;
}) {
  const list = useAsync(() => db.copyConfigs.list(), []);
  const [editing, setEditing] = useState<{ value: Form; isNew: boolean } | null>(null);
  const [running, setRunning] = useState<CopyConfig | null>(null);
  const [removing, setRemoving] = useState<CopyConfig | null>(null);
  const notice = useNotice();
  const rows = [...(list.data ?? [])].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
  const isProtected = (id: string) => !!servers.find((s) => s.id === id)?.protected;

  return (
    <>
      <div className="dbx-toolbar">
        <span className="dbx-muted">Named source → target copies with post-restore scripts.</span>
        <span className="dbx-spacer" />
        {scopes.copy && (
          <Button size="sm" onClick={() => setEditing({ value: { ...empty }, isNew: true })}>
            New copy config
          </Button>
        )}
      </div>
      <ErrorBox error={list.error} />
      <Loading loading={list.loading && !list.data} />
      {list.data && !rows.length && (
        <EmptyState
          card
          title="No copy configs yet"
          hint={scopes.copy ? 'Create one to copy e.g. a production database to a test server.' : undefined}
        />
      )}
      {!!rows.length && (
        <div className="nyk-card">
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="copy-configs">
              <thead>
                <tr>
                  <th>Config</th>
                  <th>Source</th>
                  <th>Target</th>
                  <th>Method</th>
                  <th className="nyk-ui-num">Scripts</th>
                  <th>Updated</th>
                  {scopes.copy && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-row={c.id}>
                    <td className="nyk-card-title">
                      <b>{c.name || c.id}</b>
                      <div className="nyk-ui-hint">{c.description || c.id}</div>
                    </td>
                    <td data-label="Source">
                      <Target servers={servers} server={c.sourceServerId} database={c.sourceDatabase} />
                    </td>
                    <td data-label="Target">
                      <Target servers={servers} server={c.targetServerId} database={c.targetDatabase} />
                      {isProtected(c.targetServerId) && <ProtectedChip />}
                    </td>
                    <td data-label="Method">{c.method}</td>
                    <td className="nyk-ui-num" data-label="Scripts">
                      {c.postRestoreScripts.length}
                    </td>
                    <td data-label="Updated" title={c.updatedAt}>
                      {formatRelative(c.updatedAt)}
                      <div className="nyk-ui-hint">{c.updatedBy}</div>
                    </td>
                    {scopes.copy && (
                      <td className="nyk-row-actions">
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => setRunning(c)}
                          disabled={isProtected(c.targetServerId)}
                        >
                          Run
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing({ value: toForm(c), isNew: false })}
                        >
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRemoving(c)}>
                          Delete
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        </div>
      )}
      {editing && (
        <ConfigForm
          initial={editing.value}
          isNew={editing.isNew}
          servers={servers}
          onClose={() => setEditing(null)}
          onSaved={(c) => {
            setEditing(null);
            notice.success(`Saved ${c.name || c.id}`);
            void list.reload();
          }}
        />
      )}
      {running && (
        <RunCopyDialog
          config={running}
          servers={servers}
          onClose={() => setRunning(null)}
          onStarted={(job) => {
            setRunning(null);
            onJob(job);
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Delete ${removing.name || removing.id}`}
          confirmLabel="Delete"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            await db.copyConfigs.remove(removing.id);
            setRemoving(null);
            notice.success(`Deleted ${removing.name || removing.id}`);
            void list.reload();
          }}
        >
          <p>Deletes the config only; databases and past jobs are not touched.</p>
        </ConfirmDialog>
      )}
      {notice.element}
    </>
  );
}
